import { formatOfferDate } from '@libs/common-next';
import type { Offer } from '@libs/common-types';
import { isDateInRange } from '@libs/core';

import type { ContentfulLoyaltyOfferFields, OfferCardEntry } from './types';

export const MAX_OFFER_CARDS = 4;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const isOfferCardEntry = (value: unknown): value is OfferCardEntry =>
    isRecord(value) && isRecord(value.offer) && typeof value.offer.loyaltyOfferIdReference === 'string';

const hasValidAvailabilityWindow = (
    offer: ContentfulLoyaltyOfferFields,
    date: Date
): offer is ContentfulLoyaltyOfferFields & Pick<Offer, 'availabilityEnd' | 'availabilityStart'> => {
    if (!offer.availabilityStart || !offer.availabilityEnd) {
        return false;
    }

    const startDate = new Date(offer.availabilityStart);
    const endDate = new Date(offer.availabilityEnd);

    return (
        !Number.isNaN(startDate.getTime()) &&
        !Number.isNaN(endDate.getTime()) &&
        isDateInRange(date, [startDate, endDate])
    );
};

export const mapOfferCardEntryToOffer = (entry: OfferCardEntry, date: Date): Offer | null => {
    const offer = entry.offer;

    if (!hasValidAvailabilityWindow(offer, date)) {
        return null;
    }

    const offerId = offer.loyaltyOfferIdReference;
    const name = offer.offerName ?? offer.name;
    const shortDescription = offer.shortDescription;
    const webImageUrl = offer.webImageUrl;

    if (!offerId || !name || !shortDescription || !webImageUrl) {
        return null;
    }

    return {
        activationPromoId: offer.activationPromoId ?? 0,
        availabilityEnd: offer.availabilityEnd,
        availabilityStart: offer.availabilityStart,
        callToAction: offer.callToAction ?? '',
        cardSize: offer.cardSize ?? 'Small',
        choiceRewardId: offer.choiceRewardId,
        deeplink: offer.deeplink ?? '',
        deeplinkCa: offer.deeplinkCa ?? '',
        disclaimer: offer.disclaimer ?? '',
        disclaimerRichText: offer.disclaimerRichText,
        displayRank: offer.displayRank ?? null,
        expiration: {
            title: formatOfferDate(offer.availabilityEnd),
            description: 'See details',
        },
        intType: offer.intType,
        isActivatable: true,
        isActivated: false,
        longDescription: offer.longDescription ?? '',
        longDescriptionRichText: offer.longDescriptionRichText,
        loyaltyId: offer.loyaltyId ?? '',
        memberDescriptor: offer.memberDescriptor,
        memberGroup: offer.memberGroup,
        mobileImageUrl: offer.mobileImageUrl ?? '',
        mobileImageUrlLarge: offer.mobileImageUrlLarge,
        name,
        offerId,
        offerType: offer.offerType ?? '',
        promoExternalReference: offer.promoExternalReference ?? offerId,
        promotionId: offer.promotionId,
        promoType: offer.promoType ?? 0,
        recommendedDisplayRank: offer.recommendedDisplayRank,
        shortDescription,
        size: offer.size,
        transactionType: offer.transactionType,
        version: offer.version,
        webImageUrl,
        webImageUrlFeatured: offer.webImageUrlFeatured ?? webImageUrl,
        webImageUrlLarge: offer.webImageUrlLarge ?? webImageUrl,
        webUrl: offer.webUrl ?? '',
        webUrlCa: offer.webUrlCa ?? offer.webUrlCA ?? '',
    };
};

export const prepareEligibleOffers = (cards: unknown[], date: Date): Offer[] =>
    cards
        .filter(isOfferCardEntry)
        .map(card => mapOfferCardEntryToOffer(card, date))
        .filter((offer): offer is Offer => offer !== null)
        .slice(0, MAX_OFFER_CARDS);
